import { AppCard, AppCardBody, AppText, kindredFontFamilies } from "../../ui-foundation";
import { DocumentList, DocumentPageScaffold, DocumentSection } from "@/components/DocumentScaffold";
import {
  KINDRED_SUPPORT_EMAIL,
  STEADY_HOUSE_LEGAL_NAME,
  STEADY_HOUSE_SUPPORT_URL,
} from "../../shared/support";

const CodeOfConduct = () => {
  return (
    <DocumentPageScaffold
      title="Code of Conduct"
      updatedLabel="Last updated: April 2026"
      fallbackPath="/"
    >
      <DocumentSection title="Our Commitment">
        <AppText>
          Kindred exists to provide a safe, respectful, and supportive space for single parents to connect,
          share experiences, and build community. Abuse, harassment, or behaviour that undermines safety
          will not be tolerated.
        </AppText>
        <AppText>{STEADY_HOUSE_LEGAL_NAME} is responsible for enforcing this Code through Kindred&apos;s reporting, moderation, and safeguarding processes.</AppText>
      </DocumentSection>

      <DocumentSection title="Expected Behaviour">
        <DocumentList
          items={[
            "Treat others with respect and kindness",
            "Use inclusive, welcoming language",
            "Be considerate of different parenting styles and circumstances",
            "Respect privacy and confidentiality",
            "Offer support constructively and without judgement",
            "Use reporting tools when inappropriate behaviour occurs",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="Unacceptable Behaviour">
        <DocumentList
          items={[
            "Harassment, bullying, intimidation, or repeated unwelcome contact",
            "Discrimination based on any protected characteristic",
            "Explicit, sexual, violent, or otherwise inappropriate content",
            "Spam, solicitation, or commercial promotion",
            "Sharing personal data of others without consent (doxxing)",
            "Romantic or sexual advances toward other members",
            "Impersonation or misrepresentation",
            "Fraudulent or illegal activity",
          ]}
        />
        <AppText>
          Any content or behaviour that risks member safety may be removed at our discretion.
        </AppText>
      </DocumentSection>

      <DocumentSection title="Privacy & Safety">
        <DocumentList
          items={[
            "Do not share personal contact details publicly",
            "Only upload child photos where you have the right or permission to share them",
            "Do not post identifiable photos of another member's child without their parent or carer's agreement",
            "Avoid school uniforms, school names, home addresses, number plates, or live location clues in child photos",
            "Use the block feature if you feel uncomfortable",
            "Report suspicious or concerning behaviour immediately",
            "If meeting in person, choose public venues and take precautions",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="Content Guidelines">
        <DocumentList
          items={[
            "Keep conversations relevant to single parenting, connection, and support",
            "No advertising or commercial promotion",
            "No political or religious debates",
            "No medical or legal advice beyond encouraging professional help",
            "Respect intellectual property rights when sharing content",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="Consequences of Violations">
        <DocumentList
          items={[
            "A formal warning",
            "Temporary suspension",
            "Permanent account ban",
            "Legal action where illegal behaviour is involved",
          ]}
        />
        <AppText>
          We reserve the right to remove any content or member that breaches this Code at our sole
          discretion.
        </AppText>
      </DocumentSection>

      <DocumentSection title="Reporting Violations">
        <AppText>If you experience or witness behaviour that violates this Code:</AppText>
        <DocumentList
          items={[
            "Use the block feature for immediate safety",
            "Report the user or content using the in-app reporting system",
            <>
              Contact us for urgent concerns via the{" "}
              <a
                href={STEADY_HOUSE_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="kindred-inline-link"
              >
                Steady House support page
              </a>{" "}
              or{" "}
              <a href={`mailto:${KINDRED_SUPPORT_EMAIL}`} className="kindred-inline-link">
                {KINDRED_SUPPORT_EMAIL}
              </a>
            </>,
          ]}
        />
        <AppText>All reports are treated confidentially and investigated promptly.</AppText>
      </DocumentSection>

      <DocumentSection title="Changes to This Code">
        <AppText>
          We may revise this Code of Conduct from time to time. Significant changes will be communicated
          through the app. Continued use of Kindred after changes means acceptance of the updated Code.
        </AppText>
      </DocumentSection>

      <AppCard backgroundColor="$panelBackground">
        <AppCardBody alignItems="center">
          <AppText
            textAlign="center"
            fontFamily={kindredFontFamilies.sansBold}
            fontSize="$md"
            lineHeight="$md"
          >
            Thank you for helping keep Kindred safe, respectful, and supportive for single parent
            families.
          </AppText>
        </AppCardBody>
      </AppCard>
    </DocumentPageScaffold>
  );
};

export default CodeOfConduct;
